import {
  SearchCriteriaDto,
  CriterionDto,
  CriteriaListDto,
  criterionNames,
  FilterCritria,
} from '../../../interfaces';
import * as Devices from '../../../modules/devices';
import Logger from '../../../libs/logger';  import { LogArea } from '../../../libs/logger/const';

const SearchCriteriaDtoExtended: { [key: string]: number } = {
  ...SearchCriteriaDto,
  [criterionNames.isActive]: SearchCriteriaDto.active,
  [criterionNames.deviceStatus]: SearchCriteriaDto.active,
  [criterionNames.userWithReadRights]: SearchCriteriaDto.readRights,
  [criterionNames.numDevices]: SearchCriteriaDto.itemCount,
};

const hasValue = (value: any): boolean =>
  value !== 'undefined' && value !== null && value !== '';

const mapCriterionToNumber = (key: string): number =>
  hasValue(SearchCriteriaDtoExtended[key])
    ? SearchCriteriaDtoExtended[key]
    : -1;

const buildDefaultCriterion = (
  key: string,
  value: string
): CriterionDto | null => {
  const criterionId = mapCriterionToNumber(key);
  const val = value.trim();
  return criterionId < 0 || !hasValue(val)
    ? null
    : { criteriaType: criterionId, criteriaValue: val };
};

const areEqual = (a: CriterionDto, b: CriterionDto) =>
  a.criteriaType === b.criteriaType && a.criteriaValue === b.criteriaValue;

const addCriterionToList = (
  criteria: CriterionDto[],
  newItems: CriterionDto[] | CriterionDto
) => {
  const itemsToAdd = Array.isArray(newItems) ? newItems : [newItems];
  itemsToAdd.forEach((i) => {
    if (criteria.filter((c) => areEqual(c, i)).length === 0) criteria.push(i);
    else Logger.info(['duplicated criterion', i], LogArea.Api);
  });
};

export const clearCriterionByType = (
  criteria: CriterionDto[],
  cType: number
): CriterionDto[] => criteria.filter((c) => c.criteriaType !== cType);

const csvSeparator = ',';
const rangeSeparator = '-';

const getCriteriaFromSN = (val: string) => {
  const criteria: CriterionDto[] = [];
  const startWithDevice = Devices.parsePK(val);

  const startWithManIdcriterion = startWithDevice
    ? buildDefaultCriterion(
        criterionNames.manufactureId,
        String(startWithDevice.manufactureId)
      )
    : undefined;
  startWithManIdcriterion &&
    addCriterionToList(criteria, startWithManIdcriterion);

  const startWithSNcriterion = startWithDevice
    ? buildDefaultCriterion(
        criterionNames.serialNumber,
        String(startWithDevice.serialNumber)
      )
    : undefined;
  startWithSNcriterion && addCriterionToList(criteria, startWithSNcriterion);
  return criteria;
};

const getCriteriaFromSNLabel = (val: string) => {
  const criteria: CriterionDto[] = [];
  const startWithSNLabelcriterion = buildDefaultCriterion(criterionNames.snLabel, String(val));
  startWithSNLabelcriterion && addCriterionToList(criteria, startWithSNLabelcriterion);
  return criteria;
};

export const isARange = (val: string) => val.includes(rangeSeparator);

const getCriteriaFromSNRange = (inputVal: string) => {
  const criteria: CriterionDto[] = [];
  if (isARange(inputVal)) {
    const parts = inputVal.split(rangeSeparator);
    if (parts.length === 2) {
      const startDevice = Devices.parsePK(parts[0]);
      const endDevice = Devices.parsePK(parts[1]);
      if (
        startDevice &&
        endDevice &&
        startDevice.manufactureId === endDevice.manufactureId
      ) {
        const manufactureCriterion = buildDefaultCriterion(
          criterionNames.manufactureId,
          String(startDevice.manufactureId)
        );
        manufactureCriterion &&
          addCriterionToList(criteria, manufactureCriterion);

        const fromCriterion = buildDefaultCriterion(
          criterionNames.meterRangeStart,
          String(startDevice.serialNumber)
        );
        fromCriterion && addCriterionToList(criteria, fromCriterion);

        const toCriterion = buildDefaultCriterion(
          criterionNames.meterRangeEnd,
          String(endDevice.serialNumber)
        );
        toCriterion && addCriterionToList(criteria, toCriterion);
      }
    }
  }
  return criteria;
};

export const getValuesFromSNFilter = (val: string) => val.split(csvSeparator);

export const getDevicesFromSNFilter = (value: string) => {
  const deviceIds: Devices.PKey[] = [];
  const values = getValuesFromSNFilter(value);
  if (values.length === 1) return deviceIds;
  values
    .filter((v) => !isARange(v))
    .forEach((val) => {
      const deviceId = Devices.parsePK(val);
      deviceId && deviceIds.push(deviceId);
    });
  return deviceIds;
};

export const getDevicesFromSNLabelFilter = (value: string) => {
  const labels: string[] = [];
  const values = getValuesFromSNFilter(value);
  if (values.length === 1) return labels;
  values
    .forEach((val) => {
      val && labels.push(val);
    });
  return labels;
};



export const updateCriteria = (
  criteria: CriterionDto[],
  key: string,
  value: any
): void | null => {
  switch (key) {
    case criterionNames.serialNumber:
      const values = getValuesFromSNFilter(value);
      if (values.length === 1) {
        const val = values[0];
        if (isARange(val)) {
          addCriterionToList(criteria, getCriteriaFromSNRange(val));
        } else {
          addCriterionToList(criteria, getCriteriaFromSN(val));
        }
      } else {
        values
          .map((t: string) => String(t))
          .forEach((v: string) => {
            isARange(v) &&
              addCriterionToList(criteria, getCriteriaFromSNRange(v));
          });
      }
      break;
      case criterionNames.snLabel:
        const labels = getValuesFromSNFilter(value);
        if (labels.length === 1) {
          const val = labels[0];
          addCriterionToList(criteria, getCriteriaFromSNLabel(val));
        } 
        break;
    case criterionNames.deviceStatus:
      if (value && value !== Devices.Statuses.All) {
        const criterion = buildDefaultCriterion(
          criterionNames.active,
          String(value === Devices.Statuses.Active)
        );
        criterion && addCriterionToList(criteria, criterion);
      }
      break;
    default:
      Logger.info(
        ['updateCriteria on default', key, value],
        LogArea.Api
      );
      const valItems = Array.isArray(value) ? value : [value];
      valItems.forEach((val) => {
        const defCriterion = buildDefaultCriterion(key, String(val));
        defCriterion && addCriterionToList(criteria, defCriterion);
      });
      break;
  }
};

const buildCriterionList = (criteria: any): CriterionDto[] => {
  const criterionList = Object.keys(criteria).reduce(
    (res: CriterionDto[], key: string) => {
      updateCriteria(res, key, criteria[key]);
      return res;
    },
    []
  );
  return criterionList;
};

export const buildCriteraList = (
  filterCriteria: FilterCritria,
  sortCriteria: Devices.TableSortCriterion[]
) => {
  const criteriaDtos: CriterionDto[] = buildCriterionList(filterCriteria);
  const criteriaListDto: CriteriaListDto = { criteriaDtos };
  if (sortCriteria && sortCriteria.length) {
    criteriaListDto.sortBy = mapCriterionToNumber(sortCriteria[0].id);
    criteriaListDto.sortDescending = sortCriteria[0].desc;
  }
  return criteriaListDto;
};
